import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { Gate } from '../../../global/contexts/auth/auth';
import { IconEdit } from '../../../common/icons';
import { formatDateOnly } from '../../../common/utils/date';
import { embryoStageColor, embryoStageLabel, TE_ROLE_LABEL } from '../../../core/constants/transfers';
import { PREG_STATE } from '../../../core/constants/dx';
import { useOpuSession } from '../../opu/contexts/opu';
import { useTransferSession, useOpuTransferSessions } from '../contexts/transfers';
import SessionEditModal from '../forms/session-edit-modal';
import TransferReportModal from '../forms/report-modal';

import '../../../common/styles/catalog.css';
import './styles/workspace.css';
import './styles/detail.css';

const STATUS_LABEL = { 2: 'Activa', 1: 'Inactiva', 0: 'Eliminada' };

// KPIs de la OPU (view=resolution → rollup). El endpoint de KPIs es agregado del
// hato; el "KPI de esta OPU" es su funnel de resolución.
const KPI_FIELDS = [
    { key: 'viables', label: 'Viables' },
    { key: 'embryos_total', label: 'Embriones' },
    { key: 'embryos_transferidos', label: 'Transferidos' },
    { key: 'pregnancies_confirmed', label: 'Preñadas' },
    { key: 'pregnancies_open', label: 'Preñez abierta' },
    { key: 'births_count', label: 'Partos' },
];

export default function TransferDetail() {
    const { id } = useParams();
    const [params] = useSearchParams();
    const opuId = params.get('opu');

    const { data: te, isLoading, error } = useTransferSession(id);
    const { data: opu } = useOpuSession(opuId, 'resolution');
    const { data: opuData } = useOpuTransferSessions(opuId, { enabled: !!opuId });
    const [showEdit, setShowEdit] = useState(false);
    const [showReport, setShowReport] = useState(false);

    if (isLoading) return <div className="cat-detail__state">Cargando transferencia…</div>;
    if (error || !te) return <div className="cat-detail__state">Sesión de transferencia no encontrada.</div>;

    const transfers = te.transfers || [];
    const rollup = opu?.resolution?.rollup;

    // El informe es por OPU: si hay opu, lista sus sesiones T.E. (derivado) para
    // filtrar por finca/cliente; si no, cae a esta sola sesión.
    const reportSessions = opuData?.transfer_sessions
        || [{ id: te.id, code: te.code, date: te.date, client: te.client, farm: te.farm, status: te.status, transfers: te.transfers }];

    const resumeUrl = `/opu/transfer/edit?session=${te.id}${opuId ? `&opu=${opuId}` : ''}`;

    return (
        <div className="cat-detail">
            <header className="cat-detail__header">
                <div>
                    <Link to={opuId ? `/admin/opu/${opuId}` : '/admin/opu'} className="cat-detail__back">{opuId ? 'Volver a la OPU' : 'OPU'}</Link>
                    <div className="cat-detail__title-row">
                        <h1 className="cat-detail__title">{te.code}</h1>
                        <span className={`cat-badge cat-badge--${te.status}`}>{STATUS_LABEL[te.status] || '—'}</span>
                    </div>
                    <p className="transfer-detail__sub">
                        {te.farm?.name || '—'}{te.client?.name ? ` · ${te.client.name}` : ''} · {formatDateOnly(te.date)}
                        {te.staff?.length > 0 && ` · ${te.staff.map((s) => `${s.user?.name} (${TE_ROLE_LABEL[s.role] || s.role})`).join(', ')}`}
                    </p>
                </div>
                <div className="transfer-detail__actions">
                    <Gate domain="reports" action="generate">
                        <button type="button" className="transfer-detail__btn" onClick={() => setShowReport(true)}>Imprimir PDF</button>
                    </Gate>
                    <Gate domain="transfers" action="write">
                        <a className="transfer-detail__btn" href={resumeUrl} target="_blank" rel="noopener noreferrer">Editar transferencias</a>
                        <button type="button" className="transfer-detail__btn transfer-detail__btn--primary" onClick={() => setShowEdit(true)}>
                            <IconEdit /><span>Editar sesión</span>
                        </button>
                    </Gate>
                </div>
            </header>

            {rollup && (
                <section className="cat-detail__card">
                    <h2 className="cat-detail__section-title">KPIs de la OPU {opu?.code ? `· ${opu.code}` : ''}{rollup.fase ? ` · ${rollup.fase}` : ''}</h2>
                    <div className="transfer-detail__kpis">
                        {KPI_FIELDS.map((f) => (
                            <div key={f.key} className="transfer-detail__kpi">
                                <span className="transfer-detail__kpi-value">{rollup[f.key] ?? 0}</span>
                                <span className="transfer-detail__kpi-label">{f.label}</span>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <section className="cat-detail__card">
                <h2 className="cat-detail__section-title">Transferencias ({transfers.length})</h2>
                {!transfers.length ? (
                    <p className="transfer-detail__empty">Esta sesión no tiene transferencias.</p>
                ) : (
                    <div className="transfer-table-wrap">
                        <table className="transfer-table">
                            <thead>
                                <tr>
                                    <th className="transfer-th-num">#</th>
                                    <th>Embrión</th>
                                    <th>Donadora</th>
                                    <th>Toro</th>
                                    <th>Receptora</th>
                                    <th>Preñez</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transfers.map((t, i) => {
                                    const l = t.embryo?.lineage || {};
                                    const st = PREG_STATE[t.pregnancy?.state];
                                    return (
                                        <tr key={t.id} className="transfer-tr">
                                            <td className="transfer-td-num">{i + 1}</td>
                                            <td className="transfer-td-embryo">
                                                <span className="transfer-embryo__code">{t.embryo?.code}</span>
                                                <span className="transfer-embryo__chips">
                                                    <span className="transfer-stage" style={{ '--stage': embryoStageColor(t.embryo?.stage) }}>{embryoStageLabel(t.embryo?.stage)}</span>
                                                </span>
                                            </td>
                                            <td className="transfer-td-gen"><span className="transfer-gen__name">{l.donor?.name || '—'}</span></td>
                                            <td className="transfer-td-gen"><span className="transfer-gen__name">{l.sire?.name || '—'}</span></td>
                                            <td className="transfer-td-recipient">{t.recipient ? `${t.recipient.number}${t.recipient.name ? ` · ${t.recipient.name}` : ''}` : '—'}</td>
                                            <td>{st ? <span className="transfer-detail__preg">{st.label}</span> : '—'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {showEdit && <SessionEditModal session={te} onClose={() => setShowEdit(false)} />}
            {showReport && (
                <TransferReportModal
                    opuCode={opu?.code}
                    sessions={reportSessions}
                    onClose={() => setShowReport(false)}
                />
            )}
        </div>
    );
}
